import React, { useState } from 'react';
import { ApiService } from '../services/api';

interface RegisterProps {
    onCancel: () => void;
    onSuccess: (user?: any) => void;
    intent?: 'login' | 'register' | 'buy';
    onSwitchToLogin?: () => void;
}

const Register: React.FC<RegisterProps> = ({ onCancel, onSuccess, intent, onSwitchToLogin }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        address: '',
        zip: '',
        city: '',
        country: 'Schweiz'
    });
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccessMsg('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwörter stimmen nicht überein.');
            return;
        }

        if (!acceptedTerms) {
            setError('Bitte bestätige, dass du echte Daten verwendest.');
            return;
        }

        if (!acceptedPrivacy) {
            setError('Bitte akzeptiere die Datenschutzrichtlinien.');
            return;
        }

        setLoading(true);

        try {
            const user = await ApiService.register({
                email: formData.email,
                password: formData.password,
                first_name: formData.firstName,
                last_name: formData.lastName,
                street: formData.address,
                zip_code: formData.zip,
                city: formData.city,
                country: formData.country,
                full_name: `${formData.firstName} ${formData.lastName}`.trim()
            });
            // Auto-login since the backend now returns a token immediately
            onSuccess(user); 
        } catch (err: any) {
            console.error(err);
            const msg = err.message || 'Registrierung fehlgeschlagen.';
            if (msg.toLowerCase().includes('existiert bereits')) {
                setError('Diese E-Mail ist bereits registriert. Möchtest du dich stattdessen einloggen?');
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    if (successMsg) {
        return (
            <div className="text-center p-8 animate-fadeIn">
                <div className="w-20 h-20 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
                    ✉️
                </div>
                <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-4">E-Mail Bestätigung</h2>
                <p className="text-slate-300 mb-8 max-w-sm mx-auto">
                    {successMsg}
                </p>
                <button
                    onClick={onCancel} // Back to Login
                    className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-xl font-bold uppercase tracking-widest transition-colors"
                >
                    Zurück zum Login
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4 animate-fadeIn">
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-6 text-center">Registrierung</h2>

            {error && (
                <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl mb-4 text-center">
                    <div className="text-red-400 text-sm font-bold mb-2">{error}</div>
                    {error.includes('einloggen') && onSwitchToLogin && (
                        <button 
                            type="button" 
                            onClick={onSwitchToLogin} 
                            className="mt-2 bg-slate-800 hover:bg-slate-700 text-white px-6 py-2 rounded-lg font-bold uppercase tracking-widest text-xs transition-colors"
                        >
                            Zum Login
                        </button>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Vorname</label>
                    <input
                        type="text" required name="firstName" value={formData.firstName} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all font-bold"
                        placeholder="Max"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Nachname</label>
                    <input
                        type="text" required name="lastName" value={formData.lastName} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all font-bold"
                        placeholder="Mustermann"
                    />
                </div>
            </div>

            <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Adresse</label>
                <input
                    type="text" required name="address" value={formData.address} onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all"
                    placeholder="Musterstrasse 12"
                />
            </div>

            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">PLZ</label>
                    <input
                        type="text" required name="zip" value={formData.zip} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all"
                        placeholder="3000"
                    />
                </div>
                <div className="col-span-8">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Ort</label>
                    <input
                        type="text" required name="city" value={formData.city} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all"
                        placeholder="Bern"
                    />
                </div>
            </div>

            <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Land</label>
                <select
                    name="country" value={formData.country} onChange={handleChange} title="Land auswählen"
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all font-bold appearance-none cursor-pointer"
                >
                    <option value="Schweiz"> Schweiz</option>
                    <option value="Deutschland"> Deutschland</option>
                    <option value="Österreich"> Österreich</option>
                </select>
            </div>

            {(formData.country === 'Deutschland' || formData.country === 'Österreich') && (
                <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl">
                    <div className="flex items-start gap-3">
                        <span className="text-xl">🚧</span>
                        <div>
                            <p className="text-sm font-bold text-amber-400 mb-1">Eingeschränkte Verfügbarkeit</p>
                            <p className="text-xs text-amber-300/80 leading-relaxed">
                                Streckenfliegen.app ist aktuell nur in der <strong className="text-white">Schweiz</strong> mit vollem Funktionsumfang verfügbar.
                                Wir arbeiten daran, unsere Dienste so schnell wie möglich auch in deinem Land anzubieten.
                                Du kannst die App kostenlos nutzen – <strong className="text-white">Abo-Funktionen werden freigeschaltet</strong>, sobald wir in deinem Land starten.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">E-Mail</label>
                <input
                    type="email" required name="email" value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all"
                    placeholder="pilot@example.com"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Passwort</label>
                    <input
                        type="password" required name="password" value={formData.password} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all"
                        placeholder="••••••"
                    />
                </div>
                <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1 ml-1">Bestätigen</label>
                    <input
                        type="password" required name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white outline-none focus:border-sky-500/50 transition-all"
                        placeholder="••••••"
                    />
                </div>
            </div>

            <div className="bg-sky-900/10 border border-sky-500/20 p-4 rounded-xl space-y-4">
                <label className="flex items-start gap-4 cursor-pointer group">
                    <input
                        type="checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-1 w-5 h-5 bg-slate-900 border-slate-700 rounded text-sky-500 focus:ring-offset-0 focus:ring-0 cursor-pointer shrink-0"
                    />
                    <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors leading-relaxed">
                        Ich bestätige, dass ich <strong className="text-white">echte Daten</strong> verwende. Fake-Profile oder Wegwerf-E-Mails werden ohne Vorwarnung mit samt allen Daten und Flügen etc. gelöscht.
                    </span>
                </label>
                <label className="flex items-start gap-4 cursor-pointer group">
                    <input
                        type="checkbox"
                        checked={acceptedPrivacy}
                        onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                        className="mt-1 w-5 h-5 bg-slate-900 border-slate-700 rounded text-sky-500 focus:ring-offset-0 focus:ring-0 cursor-pointer shrink-0"
                    />
                    <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors leading-relaxed">
                        Ich habe die <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAcceptedPrivacy(true); setShowPrivacy(true); }} className="text-sky-400 hover:text-sky-300 underline underline-offset-2 font-bold">Datenschutzrichtlinien</button> gelesen und stimme diesen zu.
                    </span>
                </label>
            </div>

            <div className="flex gap-4 mt-6">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex-1 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all text-sm uppercase tracking-widest"
                >
                    Abbrechen
                </button>
                <button
                    type="submit" disabled={loading}
                    className="flex-1 py-4 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-black rounded-xl shadow-lg transition-all active:scale-95 text-sm uppercase tracking-widest"
                >
                    {loading ? 'Sende...' : 'Registrieren'}
                </button>
            </div>

            {showPrivacy && (
                <div className="fixed inset-0 z-[2000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPrivacy(false)}>
                    <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 md:p-10 shadow-2xl relative animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                        <button type="button" onClick={() => setShowPrivacy(false)} className="absolute top-6 right-6 text-slate-400 hover:text-white font-bold text-sm bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg transition-colors">Schliessen</button>
                        
                        <div className="space-y-6 text-slate-300 text-sm leading-relaxed pr-8">
                            <h2 className="text-2xl md:text-3xl font-black text-white uppercase mb-8">Datenschutzvereinbarungen</h2>
                            
                            <p className="text-base text-slate-200">
                                Wir nehmen den Schutz deiner persönlichen Daten sehr ernst. Wir behandeln deine personenbezogenen Daten vertraulich und entsprechend der gesetzlichen Datenschutzvorschriften der Schweiz (neues DSG) sowie der Europäischen Datenschutz-Grundverordnung (DSGVO). Mit dem Abschluss eines Abonnements oder der Nutzung der Plattform erklärst du dich mit dieser Datenschutzerklärung vollumfänglich einverstanden.
                            </p>

                            <div>
                                <h3 className="text-lg font-bold text-white uppercase mb-2">1. Verantwortliche Stelle & Geltungsbereich</h3>
                                <p>Verantwortlich für die Datenverarbeitung auf dieser Website ist die Roth AG, Schweiz. Diese Erklärung gilt für alle Dienste, Subdomains und Applikationen unter Streckenfliegen.app.</p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-white uppercase mb-2">2. Erhebung, Verarbeitung und Speicherung von Daten</h3>
                                <p>Wir erheben und verarbeiten nur Daten, die für den Betrieb der Plattform, zur Erfüllung des Abonnements und zur Bereitstellung unserer Services notwendig sind:</p>
                                <ul className="list-disc pl-5 mt-2 space-y-1 text-slate-400">
                                    <li><strong>Bestandsdaten:</strong> Name, E-Mail-Adresse und Abonnement-Status zur Identifikation und Lizenzierung.</li>
                                    <li><strong>Flugdaten & Telemetrie:</strong> Hochgeladene IGC-Dateien, Wegpunkte, Tracks und Thermik-Daten.</li>
                                    <li><strong>Nutzungsdaten:</strong> IP-Adresse, Login-Zeitpunkte und Session-Cookies.</li>
                                </ul>
                                <p className="mt-2 text-slate-400">Diese Daten werden auf hochsicheren Servern gehostet. Deine Profilinformationen liegen in verschlüsselten Datenbanken.</p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-white uppercase mb-2">3. Analyse und Weiterverarbeitung von Flugdaten (IGC & Tracks)</h3>
                                <p>Von Nutzern hochgeladene IGC-Dateien und weitere Daten werden von unserem internen System ausgewertet. Aus diesen Daten berechnen wir globale, <strong>vollständig anonymisierte Heatmaps und Thermik-Statistiken</strong>, die der Community helfen. Zudem werden alle Daten, die der User hochlädt, zu Analysezwecken, zur stetigen Verbesserung des Systems sowie für das Training von Künstlicher Intelligenz (KI) genutzt. Durch das Hochladen eines Tracks stimmst du dieser Auswertung und Nutzung ausdrücklich zu. Ein Rückschluss auf deine Person ist in den aggregierten Heatmaps und KI-Modellen technisch ausgeschlossen.</p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-white uppercase mb-2">4. Zahlungsabwicklung (Payrexx)</h3>
                                <p>Alle Transaktionen und Abonnements werden durch unseren lizenzierten Schweizer Zahlungspartner <strong>Payrexx</strong> verarbeitet. Streckenfliegen.app selbst verarbeitet und speichert <strong>zu keinem Zeitpunkt Kreditkarteninformationen</strong>. Bei einer Zahlung werden lediglich Referenzdaten (Token-Identifikation, Rechnungsbetrag, E-Mail) verschlüsselt an Payrexx übertragen. Für die Zahlungsabwicklung gelten zusätzlich die Datenschutzbestimmungen von Payrexx.</p>
                            </div>

                            <div>
                                <h3 className="text-lg font-bold text-white uppercase mb-2">5. Weitergabe an Dritte & Haftungsausschluss</h3>
                                <p>Deine Bestandsdaten werden niemals an Dritte verkauft. Streckenfliegen.app übernimmt keine Haftung für Datenlecks, Server-Ausfälle oder Datenschutzverletzungen, die ausserhalb unseres direkten Einflussbereichs liegen (z.B. bei Zahlungsanbietern, Cloud-Providern oder durch illegale Hacker-Angriffe). Die Nutzung der Plattform erfolgt auf eigenes Risiko.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
};

export default Register;
